import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Zap, Eye, MessageCircle, AlertTriangle, Loader } from 'lucide-react';

const FirstSentenceAnalysis = ({ firstSentenceData, essayText }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // If no AI analysis data, show loading or nothing
  if (!firstSentenceData) {
    if (!essayText) return null;
    
    // Show loading state while waiting for AI analysis
    return (
      <div className="bg-indigo-50 border border-indigo-300 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 text-indigo-600">
          <Loader className="w-4 h-4 animate-spin" />
          <span className="text-sm">Analyzing opening...</span>
        </div>
      </div>
    );
  }

  const getEffectivenessColor = (score) => {
    if (score >= 8) return 'text-emerald-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHookTypeIcon = (type) => {
    const icons = {
      'dialogue': '💬',
      'scene-setting': '🎬',
      'action': '⚡',
      'philosophical': '🤔',
      'shocking-statement': '😱',
      'vulnerable-admission': '💭',
      'cryptic': '🔮',
      'humor': '😄',
      'question': '❓',
      'personal': '👤'
    };
    return icons[type] || '📝';
  };

  const getHookTypeDescription = (type) => {
    const descriptions = {
      'dialogue': 'Dialogue Opening - Drops reader into conversation',
      'scene-setting': 'Scene Setting - Places reader in vivid moment',
      'action': 'Action Opening - Immediate movement and energy',
      'philosophical': 'Philosophical - Big ideas from the start',
      'shocking-statement': 'Shocking Statement - Grabs attention instantly',
      'vulnerable-admission': 'Vulnerable Admission - Raw honesty upfront',
      'cryptic': 'Cryptic Opening - Creates intrigue and mystery',
      'humor': 'Humorous - Engages with wit or irony',
      'question': 'Question - Engages reader to think',
      'personal': 'Personal - Creates immediate connection'
    };
    return descriptions[type] || 'Opening Style';
  };

  return (
    <div className="bg-indigo-50 border border-indigo-300 rounded-lg p-4 mb-6"
         style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif'}}>
      <div className="flex items-start gap-2">
        <Zap className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-indigo-800">First Sentence Analysis</h3>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-indigo-600 hover:text-indigo-700 transition-colors flex items-center gap-1 text-xs"
            >
              {isExpanded ? (
                <>
                  <span>Hide Details</span>
                  <ChevronUp className="w-3 h-3" />
                </>
              ) : (
                <>
                  <span>Show Details</span>
                  <ChevronDown className="w-3 h-3" />
                </>
              )}
            </button>
          </div>

          {/* Opening Text */}
          <div className="mb-3 p-3 bg-white border border-indigo-200 rounded">
            <p className="text-xs text-slate-800 font-medium italic">
              "{firstSentenceData.text}"
            </p>
            {firstSentenceData.contextualText && firstSentenceData.contextualText !== firstSentenceData.text && (
              <p className="text-xs text-slate-600 mt-2">
                <span className="font-medium">Context:</span> {firstSentenceData.contextualText}
              </p>
            )}
          </div>

          {/* Effectiveness Score */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600">Effectiveness:</span>
              <span className={`text-base font-bold ${getEffectivenessColor(firstSentenceData.effectiveness)}`}>
                {firstSentenceData.effectiveness}/10
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-600">{getHookTypeIcon(firstSentenceData.hookType)}</span>
              <span className="text-indigo-700 font-medium">{getHookTypeDescription(firstSentenceData.hookType)}</span>
            </div>
          </div>

          {/* Why It Works */}
          {firstSentenceData.whyItWorks && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 mb-3">
              <p className="text-xs text-emerald-800">
                <span className="font-semibold">Why it works: </span>
                {firstSentenceData.whyItWorks}
              </p>
            </div>
          )}

          {/* Admissions Reaction */}
          {firstSentenceData.admissionsReaction && (
            <div className="bg-azure-50 border border-azure-200 rounded p-3 mb-3">
              <p className="text-xs text-azure-800">
                <span className="font-semibold">Admissions reaction: </span>
                {firstSentenceData.admissionsReaction}
              </p>
            </div>
          )}

          {/* Characteristics */}
          {firstSentenceData.characteristics && (
            <div className="flex flex-wrap gap-2 mb-3">
              {firstSentenceData.characteristics.hasImmediateImpact && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  ✅ Immediate Impact
                </span>
              )}
              {firstSentenceData.characteristics.revealsVoice && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  ✅ Shows Voice
                </span>
              )}
              {firstSentenceData.characteristics.setsExpectations && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  ✅ Sets Expectations
                </span>
              )}
              {firstSentenceData.characteristics.avoidsCliches && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  ✅ Original
                </span>
              )}
              {!firstSentenceData.characteristics.avoidsCliches && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                  ⚠️ Contains Cliché
                </span>
              )}
            </div>
          )}

          {/* Expanded Details */}
          {isExpanded && (
            <div className="mt-4 space-y-3 border-t border-indigo-200 pt-3">
              {/* Strengths */}
              {firstSentenceData.strengths && firstSentenceData.strengths.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-emerald-700 mb-2">Strengths</h4>
                  <ul className="space-y-1">
                    {firstSentenceData.strengths.map((strength, idx) => (
                      <li key={idx} className="text-xs text-slate-700 flex items-start gap-1">
                        <span className="text-emerald-600 mt-0.5">✓</span>
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Areas for Improvement */}
              {firstSentenceData.improvements && firstSentenceData.improvements.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-amber-700 mb-2">Areas for Improvement</h4>
                  <ul className="space-y-1">
                    {firstSentenceData.improvements.map((improvement, idx) => (
                      <li key={idx} className="text-xs text-slate-700 flex items-start gap-1">
                        <span className="text-amber-600 mt-0.5">⚠</span>
                        <span>{improvement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FirstSentenceAnalysis;